import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { mensagemErro } from '../api/axios';
import {
  cancelarRecuperacao,
  detalheDoTitulo,
  estornarRetido,
  evolucaoDoTitulo,
  informarRetido,
} from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Painel,
  Tabela,
  Th,
} from '../components/common';
import GraficoEvolucaoTitulo from '../components/GraficoEvolucaoTitulo';
import {
  brl,
  competenciaValida,
  erroCompetencia,
  formatDateTime,
  mascararCompetencia,
  mascararMoeda,
  moedaParaNumero,
  normalizarMoeda,
  numero,
} from '../utils/formato';

const ICONE = {
  titulo_identificado: 'bi-link-45deg',
  titulo_alterado: 'bi-arrow-repeat',
  recebimento: 'bi-cash-coin',
  glosa: 'bi-scissors',
  retido_informado: 'bi-bank',
  retido_estornado: 'bi-arrow-counterclockwise',
  conferido: 'bi-check2-circle',
  recuperacao_confirmada: 'bi-arrow-up-circle',
};

/**
 * O título inteiro, em página própria.
 *
 * Era uma caixa flutuante, e deixou de caber: com o gráfico de evolução, os
 * indicadores, a glosa por causa e a linha do tempo, o diálogo virava uma
 * segunda tela dentro da primeira. Página tem URL — o analista salva o
 * título, volta nele e manda para alguém sem precisar explicar o caminho.
 */
export default function TituloDetalhe() {
  const { protocolo } = useParams();
  const navegar = useNavigate();
  const [dados, setDados] = useState(null);
  const [evolucao, setEvolucao] = useState(null);
  const [erro, setErro] = useState('');
  const [agindo, setAgindo] = useState(false);
  const [novoRetido, setNovoRetido] = useState(null);
  const [erroComp, setErroComp] = useState('');

  const carregar = useCallback(() => {
    // Em paralelo: a evolução é mais cara e não deve atrasar as três contas.
    detalheDoTitulo(protocolo)
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível abrir o título.')));
    evolucaoDoTitulo(protocolo)
      .then((r) => setEvolucao(r.data))
      .catch(() => setEvolucao(null));
  }, [protocolo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const lancarRetido = async () => {
    setAgindo(true);
    setErro('');
    try {
      await informarRetido(protocolo, {
        // A máscara é texto ("2.500,00"); o que vai para a API é número.
        valor: moedaParaNumero(novoRetido.valor),
        justificativa: novoRetido.justificativa,
        competencia: novoRetido.competencia || null,
      });
      setNovoRetido(null);
      setErroComp('');
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível lançar a retenção.'));
    } finally {
      setAgindo(false);
    }
  };

  const estornar = async (id) => {
    const motivo = window.prompt('Por que este lançamento está sendo estornado?');
    if (!motivo) return;
    setAgindo(true);
    try {
      await estornarRetido(id, motivo);
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível estornar.'));
    } finally {
      setAgindo(false);
    }
  };

  const desfazerRecuperacao = async (id) => {
    const motivo = window.prompt(
      'Por que este vínculo não é uma recuperação? A glosa volta ao valor cheio.'
    );
    if (!motivo) return;
    setAgindo(true);
    try {
      await cancelarRecuperacao(id, motivo);
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível desfazer.'));
    } finally {
      setAgindo(false);
    }
  };

  const t = dados?.titulo;
  const ind = evolucao?.indicadores;

  return (
    <>
      <PageHeader
        titulo={`Título ${protocolo}`}
        subtitulo={
          t
            ? `${t.operadora} · ${t.rotulo_estado}${
                t.confirmado_em
                  ? ` · lançado em ${formatDateTime(t.confirmado_em)} por ${
                      t.confirmado_por
                    }`
                  : ' · nunca lançado'
              }`
            : 'Carregando…'
        }
        acoes={
          <Botao
            variante="secundario"
            icone="bi-arrow-left"
            onClick={() => navegar('/titulos')}
          >
            Voltar aos títulos
          </Botao>
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {!dados ? (
        <LoadingSpinner />
      ) : (
        <div className="d-flex flex-column gap-3">
          {t.recuperacao_pendente_qtd > 0 && (
            <Alerta tipo="atencao">
              Há <strong>{brl(t.recuperacao_pendente_valor)}</strong> em{' '}
              {numero(t.recuperacao_pendente_qtd)} item(ns) que o sistema
              identificou como glosa recuperada neste título —{' '}
              <strong>ainda não confirmados</strong>. Enquanto ninguém
              confirmar, esse valor continua contando como glosa, e a conta
              <em> Recuperado</em> fica zerada.{' '}
              <Link
                to={`/recuperacoes?protocolo=${encodeURIComponent(protocolo)}`}
              >
                Ver e decidir
              </Link>
              .
            </Alerta>
          )}

          <Painel
            titulo="As contas do título"
            subtitulo="Recebido, glosado e retido são as três baixas; em aberto é o que sobra."
          >
            <div className="ff-grid-tiles">
              <Conta rotulo="Título" valor={t.total_cobrado} nota="Bioslab" />
              <Conta rotulo="Recebido" valor={t.recebido} mudanca={acha(t, 'recebido')} />
              <Conta
                rotulo="Glosado"
                valor={t.glosado}
                mudanca={acha(t, 'glosado')}
                nota={
                  t.glosado_recuperado > 0
                    ? `${brl(t.glosado_original)} recusados, ${brl(
                        t.glosado_recuperado
                      )} recuperados`
                    : undefined
                }
              />
              <Conta
                rotulo="Recuperado"
                valor={t.glosado_recuperado}
                nota={
                  Number(t.glosado_recuperado) > 0
                    ? 'voltou depois do recurso · já conta dentro do recebido'
                    : t.recuperacao_pendente_qtd > 0
                      ? `${brl(t.recuperacao_pendente_valor)} proposto, aguardando confirmação`
                      : 'nenhuma glosa voltou por recurso'
                }
              />
              <Conta rotulo="Retido" valor={t.retido} mudanca={acha(t, 'retido')} />
              <Conta rotulo="Em aberto" valor={t.saldo} nota="o que falta receber" />
            </div>
          </Painel>

          {ind && (
            <Painel
              titulo="Leitura do título"
              subtitulo="O que decide o próximo passo: cobrar, recorrer ou encerrar."
            >
              <div className="ff-grid-tiles">
                <Indicador
                  rotulo="Recebido do faturado"
                  valor={pct(ind.pct_recebido)}
                  natureza={faixa(ind.pct_recebido, 90, 60)}
                  nota={`${numero(ind.qtd_recebimentos)} entrada(s)`}
                />
                <Indicador
                  rotulo="Glosado do faturado"
                  valor={pct(ind.pct_glosado)}
                  natureza={ind.pct_glosado > 5 ? 'atencao' : 'neutro'}
                  nota={
                    ind.glosa_original > 0
                      ? `${brl(ind.glosa_original)} recusados`
                      : 'nenhuma glosa'
                  }
                />
                <Indicador
                  rotulo="Recuperação da glosa"
                  valor={pct(ind.taxa_recuperacao)}
                  natureza={ind.taxa_recuperacao > 0 ? 'entrada' : 'neutro'}
                  nota={
                    ind.glosa_original > 0
                      ? `${brl(ind.glosa_recuperada)} de volta`
                      : 'sem glosa a recorrer'
                  }
                />
                <Indicador
                  rotulo="Parado há"
                  valor={
                    ind.meses_sem_receber === null ||
                    ind.meses_sem_receber === undefined
                      ? '—'
                      : `${numero(ind.meses_sem_receber)} m`
                  }
                  natureza={faixaInversa(ind.meses_sem_receber, 6, 3)}
                  nota={
                    ind.ultima_competencia
                      ? `última entrada em ${ind.ultima_competencia}`
                      : 'nunca recebeu'
                  }
                />
              </div>
            </Painel>
          )}

          <Painel
            titulo="Evolução mês a mês"
            subtitulo="Como o título foi se preenchendo, competência a competência."
          >
            {evolucao ? (
              <GraficoEvolucaoTitulo
                meses={evolucao.meses}
                totalCobrado={evolucao.total_cobrado}
              />
            ) : (
              <LoadingSpinner />
            )}
            {evolucao && evolucao.total_cobrado === null && (
              <Alerta tipo="atencao">
                O Bioslab ainda não respondeu o total cobrado deste protocolo,
                então o gráfico usa a maior pilha como teto e não há
                &quot;em aberto&quot; a mostrar.
              </Alerta>
            )}
          </Painel>

          {evolucao?.glosa_por_causa?.length > 0 && (
            <Painel
              titulo="Glosa por causa"
              subtitulo="Causa de faturamento se corrige na origem; causa de elegibilidade vira recurso."
            >
              <Tabela estreita>
                <thead>
                  {/* sem-ordenacao: lista curta já ordenada pelo valor, que é
                      a ordem em que se ataca o problema. */}
                  <tr>
                    <Th>Causa</Th>
                    <Th alinhar="right">Itens</Th>
                    <Th alinhar="right">Valor</Th>
                  </tr>
                </thead>
                <tbody>
                  {evolucao.glosa_por_causa.map((c) => (
                    <tr key={c.codigo_causa || 'sem'}>
                      <td>
                        <span className="ff-mono">{c.codigo_causa || '—'}</span>
                        {c.motivo && (
                          <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                            {c.motivo}
                          </div>
                        )}
                      </td>
                      <td className="col-qtd">{numero(c.itens)}</td>
                      <td className="col-valor">{brl(c.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
              <p style={{ fontSize: 12, color: 'var(--ff-muted)', margin: '8px 0 0' }}>
                Estes são os valores <strong>originalmente recusados</strong>. O
                que já voltou por recurso aparece em{' '}
                <Link to="/recuperacoes">Recuperação de glosa</Link>.
              </p>
            </Painel>
          )}

          <Painel
            titulo="Retenção judicial"
            subtitulo="A única das três contas que não vem de arquivo."
            acoes={
              !novoRetido && (
                <Botao
                  variante="secundario"
                  pequeno
                  icone="bi-plus-lg"
                  onClick={() =>
                    setNovoRetido({ valor: '', justificativa: '', competencia: '' })
                  }
                >
                  Lançar retenção
                </Botao>
              )
            }
          >
            {novoRetido && (
              <div
                className="mb-3 p-3 d-flex flex-wrap gap-3 align-items-end"
                style={{ border: '1px solid var(--ff-border-soft)', borderRadius: 10 }}
              >
                <div style={{ width: 160 }}>
                  <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Valor</label>
                  <input
                    className="ff-input ff-mono"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={novoRetido.valor}
                    onChange={(e) =>
                      setNovoRetido({
                        ...novoRetido,
                        valor: mascararMoeda(e.target.value),
                      })
                    }
                    // Completa os centavos ao sair: "2.500" vira "2.500,00".
                    onBlur={(e) =>
                      setNovoRetido((r) => ({
                        ...r,
                        valor: normalizarMoeda(e.target.value),
                      }))
                    }
                  />
                </div>
                <div style={{ width: 160 }}>
                  <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                    Competência
                  </label>
                  <input
                    className="ff-input ff-mono"
                    inputMode="numeric"
                    placeholder="MM/AAAA"
                    value={novoRetido.competencia}
                    onChange={(e) => {
                      setNovoRetido({
                        ...novoRetido,
                        competencia: mascararCompetencia(e.target.value),
                      });
                      setErroComp('');
                    }}
                    onBlur={(e) => setErroComp(erroCompetencia(e.target.value))}
                  />
                  {erroComp && (
                    <span style={{ fontSize: 12, color: 'var(--ff-error-ink)' }}>
                      {erroComp}
                    </span>
                  )}
                </div>
                <div style={{ minWidth: 260, flex: '1 1 260px' }}>
                  <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                    Justificativa (obrigatória)
                  </label>
                  <input
                    className="ff-input"
                    value={novoRetido.justificativa}
                    onChange={(e) =>
                      setNovoRetido({ ...novoRetido, justificativa: e.target.value })
                    }
                    placeholder="Processo, ofício, determinação…"
                  />
                </div>
                <div className="d-flex gap-2">
                  <Botao
                    onClick={lancarRetido}
                    disabled={
                      agindo
                      || !moedaParaNumero(novoRetido.valor)
                      || !novoRetido.justificativa.trim()
                      // Competência é opcional, mas se foi digitada tem que
                      // ser válida — senão a retenção cai no mês errado do
                      // gráfico, e nada na tela acusaria.
                      || (!!novoRetido.competencia
                          && !competenciaValida(novoRetido.competencia))
                    }
                  >
                    Lançar
                  </Botao>
                  <Botao variante="secundario" onClick={() => setNovoRetido(null)}>
                    Cancelar
                  </Botao>
                </div>
              </div>
            )}

            {dados.retido.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--ff-muted)', margin: 0 }}>
                Nenhuma retenção lançada. Ela não vem de arquivo — é sempre manual.
              </p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {dados.retido.map((m) => (
                  <div
                    key={m.id_movimento}
                    className="d-flex flex-wrap align-items-center gap-3 p-2"
                    style={{
                      border: '1px solid var(--ff-border-soft)',
                      borderRadius: 8,
                      opacity: m.estornado ? 0.6 : 1,
                    }}
                  >
                    <span
                      className="ff-mono"
                      style={{
                        fontWeight: 600,
                        minWidth: 110,
                        textAlign: 'right',
                        color: m.e_estorno ? 'var(--ff-muted)' : undefined,
                      }}
                    >
                      {brl(m.valor)}
                    </span>
                    <span style={{ flex: 1, minWidth: 200, fontSize: 13 }}>
                      {m.justificativa}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                      {m.competencia ? `competência ${m.competencia}` : 'sem competência'}
                      {' · '}
                      {m.informado_por}
                    </span>
                    {m.estornado && <Badge tom="arquivado">estornado</Badge>}
                    {!m.estornado && !m.e_estorno && (
                      <Botao
                        variante="secundario"
                        pequeno
                        onClick={() => estornar(m.id_movimento)}
                        disabled={agindo}
                      >
                        Estornar
                      </Botao>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Painel>

          {dados.recuperacoes?.length > 0 && (
            <Painel
              titulo="Glosa recuperada"
              subtitulo="Itens recusados que voltaram a ser pagos depois do recurso."
            >
              <p style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                É o que explica a conta de glosa deste título ser menor que a da
                tela de Glosas, onde o valor original permanece.
              </p>
              <div className="d-flex flex-column gap-2">
                {dados.recuperacoes.map((r) => (
                  <div
                    key={r.id_recuperacao}
                    className="d-flex flex-wrap align-items-center gap-3 p-2"
                    style={{
                      border: '1px solid var(--ff-border-soft)',
                      borderRadius: 8,
                      opacity: r.ativa ? 1 : 0.6,
                    }}
                  >
                    <span
                      className="ff-mono"
                      style={{ fontWeight: 600, minWidth: 110, textAlign: 'right' }}
                    >
                      {brl(r.valor_recuperado)}
                    </span>
                    <span style={{ flex: 1, minWidth: 220, fontSize: 13 }}>
                      guia {r.guia}
                      {r.codigo_servico ? ` · ${r.codigo_servico}` : ''}
                      {r.descricao_servico ? ` · ${r.descricao_servico}` : ''}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                      glosada em {r.competencia_glosa} · paga em {r.competencia_pagamento}
                    </span>
                    {r.ativa ? (
                      <Botao
                        variante="secundario"
                        pequeno
                        onClick={() => desfazerRecuperacao(r.id_recuperacao)}
                        disabled={agindo}
                      >
                        Não é recuperação
                      </Botao>
                    ) : (
                      <Badge tom="arquivado">desfeita</Badge>
                    )}
                  </div>
                ))}
              </div>
            </Painel>
          )}

          <Painel
            titulo="Linha do tempo"
            subtitulo="Do mais antigo para o mais recente. Cada linha diz o que mudou e em qual conta."
          >
            {dados.linha_do_tempo.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--ff-muted)', margin: 0 }}>
                Nada aconteceu com este título ainda.
              </p>
            ) : (
              <ol className="ff-timeline">
                {dados.linha_do_tempo.map((e) => (
                  <Evento key={e.id_evento} e={e} />
                ))}
              </ol>
            )}
          </Painel>
        </div>
      )}
    </>
  );
}

const acha = (t, conta) => (t.mudancas || []).find((m) => m.conta === conta);

const pct = (v) => (v === null || v === undefined ? '—' : `${String(v).replace('.', ',')}%`);

/** Verde acima de `bom`, âmbar acima de `ok`, vermelho abaixo. */
function faixa(v, bom, ok) {
  if (v === null || v === undefined) return 'neutro';
  if (v >= bom) return 'entrada';
  if (v >= ok) return 'atencao';
  return 'critico';
}

/** O inverso: quanto MAIOR, pior. Usado em "parado há N meses". */
function faixaInversa(v, ruim, atencao) {
  if (v === null || v === undefined) return 'neutro';
  if (v >= ruim) return 'critico';
  if (v >= atencao) return 'atencao';
  return 'entrada';
}

function Conta({ rotulo, valor, nota, mudanca }) {
  return (
    <div className="ff-tile">
      <span className="rotulo">{rotulo}</span>
      <span className="valor">{valor === null ? '—' : brl(valor)}</span>
      {mudanca ? (
        <span className="nota" style={{ color: '#9a5b06' }}>
          era {brl(mudanca.de)} na última confirmação
        </span>
      ) : (
        nota && <span className="nota">{nota}</span>
      )}
    </div>
  );
}

/**
 * Um acontecimento do título, em quatro partes — cada uma respondendo UMA
 * pergunta: o que aconteceu · de qual documento · que conta se mexeu e para
 * onde · quando.
 */
function Evento({ e }) {
  const temDe = e.valor_de !== null && e.valor_de !== undefined;
  const temPara = e.valor_para !== null && e.valor_para !== undefined;
  const delta = temDe && temPara ? Number(e.valor_para) - Number(e.valor_de) : null;
  const destaque =
    delta !== null
      ? `${delta < 0 ? '−' : '+'} ${brl(Math.abs(delta))}`
      : temPara
        ? brl(e.valor_para)
        : null;
  const origem = [
    e.competencia ? `competência ${e.competencia}` : null,
    e.arquivo,
  ].filter(Boolean);

  return (
    <li>
      <i className={`bi ${ICONE[e.tipo] || 'bi-dot'}`} aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="d-flex flex-wrap align-items-baseline gap-2">
          <span style={{ fontWeight: 600, fontSize: 13 }}>{e.rotulo}</span>
          {e.aguardando_confirmacao && (
            <Badge tom="pendente">aguardando confirmação da revisão</Badge>
          )}
          {destaque && (
            <span className="ff-mono ms-auto" style={{ fontWeight: 600, fontSize: 13 }}>
              {destaque}
            </span>
          )}
        </div>

        {origem.length > 0 && <div style={{ fontSize: 12.5 }}>{origem.join(' · ')}</div>}

        {delta !== null && (
          <div style={{ fontSize: 12.5, color: 'var(--ff-muted)' }}>
            {e.rotulo_conta ? `${e.rotulo_conta}: ` : ''}
            <span className="ff-mono">{brl(e.valor_de)}</span>
            {' → '}
            <span className="ff-mono">{brl(e.valor_para)}</span>
          </div>
        )}

        {e.descricao && <div style={{ fontSize: 13 }}>{e.descricao}</div>}

        <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
          {formatDateTime(e.ocorrido_em)}
          {e.autor ? ` · ${e.autor}` : ''}
        </div>
      </div>
    </li>
  );
}
